import { Component, OnInit, Input, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../../../shared/core/state';
import { selectIsLoggedIn } from '../../../shared/core/state/users/user.selectors';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TenantHeroMetric, TenantHeroSettings } from '../../../shared/services/tenant.service';

@Component({
  selector: 'black-hero-section',
  templateUrl: './hero-section.component.html',
  styleUrls: ['./hero-section.component.css'],
  standalone: true,
  imports: [CommonModule, RouterModule],
})
export class HeroSectionComponent implements OnInit {
  private store = inject<Store<State>>(Store);
  private sanitizer = inject(DomSanitizer);

  @Input() customHero?: TenantHeroSettings;
  @Input() isDefaultTenant = true;
  @Input() tenantName?: string;
  @Input() theme: 'modern' | 'warm' | 'bold' = 'modern';
  @Input() instructorAvatar?: string;
  @Input() instructorName?: string;
  @Input() instructorTitle?: string;

  isUserLoggedIn = false;

  get backgroundImageUrl(): string | undefined {
    return this.customHero?.backgroundImageUrl;
  }

  get hasCustomBackground(): boolean {
    return Boolean(this.backgroundImageUrl && this.backgroundImageUrl.trim());
  }

  ngOnInit(): void {
    this.store.select(selectIsLoggedIn).subscribe((loggedIn) => {
      this.isUserLoggedIn = loggedIn;
    });
  }

  get badgeText(): string {
    if (this.customHero?.badgeText) return this.customHero.badgeText;
    if (this.isDefaultTenant) return "Sri Lanka's Premier Online Education Platform";
    return `${this.tenantName || 'Academy'} Online Learning`;
  }

  get heroTitle(): string {
    if (this.customHero?.title) return this.customHero.title;
    if (this.isDefaultTenant) return 'Empower Your Future With Modern Online Learning.';
    return `Welcome to ${this.tenantName || 'Our Academy'}`;
  }

  get heroSubtitle(): string {
    if (this.customHero?.subtitle) return this.customHero.subtitle;
    if (this.isDefaultTenant) {
      return 'Connect with top-rated Sri Lankan instructors, participate in live interactive Zoom classes, access structured course materials, and excel in your exams.';
    }
    return 'Participate in live interactive classes, access structured course materials, and achieve academic excellence.';
  }

  get ctaText(): string {
    return this.customHero?.primaryCtaText || 'Browse Live Classes';
  }

  get ctaLink(): string {
    return this.customHero?.primaryCtaLink || '/live-classes';
  }

  get secondaryCtaText(): string | undefined {
    return this.customHero?.secondaryCtaText?.trim() || undefined;
  }

  get secondaryCtaLink(): string {
    return this.customHero?.secondaryCtaLink?.trim() || '/live-classes';
  }

  normalizeIconClass(icon?: string): string {
    if (!icon || !icon.trim()) return 'fa-solid fa-circle-check';
    const trimmed = icon.trim();
    if (
      trimmed.startsWith('fa-solid ') ||
      trimmed.startsWith('fa-regular ') ||
      trimmed.startsWith('fa-brands ') ||
      trimmed.startsWith('fas ') ||
      trimmed.startsWith('far ') ||
      trimmed.startsWith('fab ')
    ) {
      return trimmed;
    }
    if (trimmed.startsWith('fa-')) {
      return `fa-solid ${trimmed}`;
    }
    return `fa-solid fa-${trimmed}`;
  }

  get secondaryCtaIcon(): string {
    return this.normalizeIconClass(this.customHero?.secondaryCtaIcon || 'fa-brands fa-whatsapp');
  }

  get resolvedSecondaryCtaLink(): string {
    const raw = this.secondaryCtaLink;
    if (!raw) return '/live-classes';
    if (
      raw.startsWith('http://') ||
      raw.startsWith('https://') ||
      raw.startsWith('mailto:') ||
      raw.startsWith('tel:')
    ) {
      return raw;
    }
    if (raw.startsWith('wa.me') || raw.startsWith('chat.whatsapp.com')) {
      return `https://${raw}`;
    }
    if (
      raw.startsWith('+') ||
      (this.secondaryCtaIcon.includes('whatsapp') && raw.replace(/[^0-9]/g, '').length >= 9)
    ) {
      let digits = raw.replace(/[^0-9]/g, '');
      if (digits.startsWith('0') && digits.length === 10) {
        digits = '94' + digits.substring(1);
      }
      return `https://wa.me/${digits}`;
    }
    return raw;
  }

  isExternalLink(url?: string): boolean {
    if (!url) return false;
    return (
      url.startsWith('http://') ||
      url.startsWith('https://') ||
      url.startsWith('mailto:') ||
      url.startsWith('tel:') ||
      url.startsWith('wa.me') ||
      url.startsWith('chat.whatsapp.com') ||
      (this.secondaryCtaIcon.includes('whatsapp') && url.replace(/[^0-9]/g, '').length >= 9)
    );
  }

  get showBadge(): boolean {
    return this.customHero?.showBadge !== false;
  }

  get showMetrics(): boolean {
    return this.customHero?.showMetrics !== false;
  }

  get mediaType(): 'portrait' | 'video' | 'batch-card' | 'custom-image' {
    if (this.customHero?.mediaType) {
      return this.customHero.mediaType;
    }
    if (!this.isDefaultTenant && this.instructorAvatar) {
      return 'portrait';
    }
    return 'batch-card';
  }

  get heroMetrics(): TenantHeroMetric[] {
    if (this.customHero?.metrics && this.customHero.metrics.length > 0) {
      return this.customHero.metrics.map((m) => ({
        ...m,
        icon: this.normalizeIconClass(m.icon),
      }));
    }
    return [
      {
        icon: 'fa-solid fa-video',
        title: 'Live Zoom Stream',
        subtitle: 'Interactive lectures',
      },
      {
        icon: 'fa-solid fa-user-graduate',
        title: this.isDefaultTenant ? 'Verified Tutors' : 'Curated Syllabus',
        subtitle: this.isDefaultTenant
          ? 'Expert Sri Lankan faculty'
          : 'Structured lessons & notes',
      },
      {
        icon: 'fa-solid fa-clock-rotate-left',
        title: 'Class Replays',
        subtitle: 'Study on your schedule',
      },
    ];
  }

  get portraitTag(): string {
    return this.customHero?.portraitTag?.trim() || 'Admissions Open';
  }

  get portraitName(): string {
    return (
      this.customHero?.portraitTitle?.trim() ||
      this.instructorName ||
      this.tenantName ||
      'Lead Instructor'
    );
  }

  get portraitTitle(): string {
    return (
      this.customHero?.portraitSubtitle?.trim() ||
      this.instructorTitle ||
      'Senior Subject Specialist'
    );
  }

  get portraitHighlights(): string[] {
    if (
      this.customHero?.portraitHighlights &&
      this.customHero.portraitHighlights.length > 0
    ) {
      return this.customHero.portraitHighlights;
    }
    return [
      'Live Interactive Zoom Lectures & Discussions',
      'Comprehensive Monthly Theory Tutes & Paper Drills',
      '1080p Full Session Recordings Available 24/7',
    ];
  }

  get mediaCustomImageUrl(): string | undefined {
    return this.customHero?.mediaCustomImageUrl?.trim() || undefined;
  }

  get safeVideoEmbedUrl(): SafeResourceUrl | null {
    const raw = this.customHero?.mediaVideoUrl?.trim();
    if (!raw) return null;

    // YouTube (standard, youtu.be, embed, shorts, live)
    const ytMatch = raw.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/,
    );
    if (ytMatch) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${ytMatch[1]}`,
      );
    }

    // Vimeo (standard, player, channels, groups)
    const vimeoMatch = raw.match(
      /(?:vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)/,
    );
    if (vimeoMatch) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      );
    }

    return null;
  }
}
